import type { BrokerPreset, BrokerSetting, CrossPlanStatus, MarginType } from "@yct/core";

/**
 * Minimal API client. On the web the SPA and the Worker share an origin; in
 * the Capacitor shell requests go to the production origin.
 */
const base = import.meta.env.VITE_API_ORIGIN ?? "";

async function request<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const { json, ...rest } = init ?? {};
  const res = await fetch(base + path, {
    credentials: "include",
    ...rest,
    headers: { ...(json !== undefined ? { "content-type": "application/json" } : {}), ...(rest.headers ?? {}) },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: unknown };
    throw new ApiError(res.status, describeError(body.error) ?? res.statusText);
  }
  return (await res.json()) as T;
}

/** Validation errors arrive as a ZodError-like object; turn them into one readable line. */
function describeError(e: unknown): string | undefined {
  if (!e) return undefined;
  if (typeof e === "string") return e;
  if (typeof e === "object") {
    const o = e as { issues?: Array<{ path?: unknown[]; message?: string }>; message?: string };
    if (Array.isArray(o.issues) && o.issues.length > 0) {
      return o.issues.map((i) => `${(i.path ?? []).join(".")}${i.path?.length ? ": " : ""}${i.message ?? ""}`).join(" / ");
    }
    if (typeof o.message === "string") return o.message;
    try {
      return JSON.stringify(e);
    } catch {
      return undefined;
    }
  }
  return String(e);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

// ---- types mirrored from the worker ----------------------------------------

export type Broker = BrokerSetting & { presetId: string | null };

export interface Holding {
  id: string;
  stockCode: string;
  stockName: string | null;
  brokerSettingId: string | null;
  shares: number;
  acquiredOn: string | null;
  unitPrice: number | null;
  source: string;
  note: string | null;
}

export interface CrossPlan {
  id: string;
  stockCode: string;
  stockName: string | null;
  lastTradingDay: string;
  brokerSettingId: string | null;
  marginType: MarginType;
  shares: number;
  openDate: string;
  closeDate: string | null;
  priceAtPlan: number;
  estGyakuhibuPerShare: number | null;
  dividendPerShare: number | null;
  estFee: number;
  estLoanCost: number;
  estDividendAdj: number;
  estTotal: number;
  actualGyakuhibu: number | null;
  actualFee: number | null;
  actualLoanCost: number | null;
  actualDividendAdj: number | null;
  actualCloseDate: string | null;
  notExecuted: boolean;
  note: string | null;
  status: CrossPlanStatus;
  needsCloseReminder: boolean;
}

export interface YutaiItem {
  id: string;
  stockCode: string;
  stockName: string | null;
  recordDate: string;
  origin: "holding" | "cross" | "manual";
  tierMinShares: number;
  description: string;
  valueYen: number | null;
  expectedArrival: string | null;
  arrivedOn: string | null;
  expiresOn: string | null;
  state: "waiting" | "on_hand" | "used";
  usedOn: string | null;
  note: string | null;
}

export interface UserSettings {
  dividendTaxRate: number;
  notifyLastTradingDay: boolean;
  notifyExDate: boolean;
  notifyExpiryDays: number[];
  notifyHour: number;
  showUsedYutai: boolean;
  adFreeUntil: string | null;
  csvExportUntil: string | null;
}

export interface ImportResult {
  added: string[];
  unchanged: string[];
  needsReview: Array<{ code: string; csvShares: number; lotShares: number }>;
  sells: Array<{ code: string; shares: number; tradeDate: string }>;
}

// ---- endpoints ---------------------------------------------------------------

export const api = {
  health: () => request<{ ok: boolean; time: string }>("/api/health"),
  me: () => request<{ user: { id: string; email: string; name: string } }>("/api/me"),
  settings: () => request<UserSettings>("/api/me/settings"),
  updateSettings: (patch: Partial<UserSettings>) => request<{ ok: true }>("/api/me/settings", { method: "PATCH", json: patch }),
  deleteAccount: () => request<{ ok: true }>("/api/me/account", { method: "DELETE" }),

  favorites: () => request<{ favorites: Array<{ stockCode: string; addedAt: string }> }>("/api/me/favorites"),
  addFavorite: (code: string) => request<{ ok: true }>(`/api/me/favorites/${code}`, { method: "PUT" }),
  removeFavorite: (code: string) => request<{ ok: true }>(`/api/me/favorites/${code}`, { method: "DELETE" }),

  brokerPresets: () => request<{ presets: BrokerPreset[] }>("/api/me/brokers/presets"),
  brokers: () => request<{ brokers: Broker[] }>("/api/me/brokers"),
  createBroker: (body: { presetId: string; name?: string }) => request<{ id: string }>("/api/me/brokers", { method: "POST", json: body }),
  updateBroker: (id: string, body: { name?: string; config?: Partial<Omit<BrokerSetting, "id" | "name">> }) =>
    request<{ ok: true }>(`/api/me/brokers/${id}`, { method: "PATCH", json: body }),
  deleteBroker: (id: string) => request<{ ok: true }>(`/api/me/brokers/${id}`, { method: "DELETE" }),

  holdings: () => request<{ holdings: Holding[] }>("/api/me/holdings"),
  createHolding: (body: Omit<Holding, "id" | "source">) => request<{ id: string }>("/api/me/holdings", { method: "POST", json: body }),
  updateHolding: (id: string, body: Partial<Omit<Holding, "id" | "source">>) =>
    request<{ ok: true }>(`/api/me/holdings/${id}`, { method: "PATCH", json: body }),
  deleteHolding: (id: string) => request<{ ok: true }>(`/api/me/holdings/${id}`, { method: "DELETE" }),
  importHoldings: (body: {
    brokerSettingId: string;
    balances?: Array<{ code: string; name: string; shares: number; unitPrice?: number }>;
    trades?: Array<{ code: string; name: string; side: "buy" | "sell"; shares: number; price: number; tradeDate: string }>;
  }) => request<ImportResult>("/api/me/holdings/import", { method: "POST", json: body }),

  crossPlans: (range?: { from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (range?.from) q.set("from", range.from);
    if (range?.to) q.set("to", range.to);
    const qs = q.toString();
    return request<{ plans: CrossPlan[] }>(`/api/me/cross-plans${qs ? `?${qs}` : ""}`);
  },
  createCrossPlan: (body: CrossPlanInput) => request<{ id: string }>("/api/me/cross-plans", { method: "POST", json: body }),
  updateCrossPlan: (id: string, body: Partial<CrossPlanInput> & CrossPlanActuals) =>
    request<{ ok: true }>(`/api/me/cross-plans/${id}`, { method: "PATCH", json: body }),
  deleteCrossPlan: (id: string) => request<{ ok: true }>(`/api/me/cross-plans/${id}`, { method: "DELETE" }),

  yutaiItems: (includeUsed: boolean) => request<{ items: YutaiItem[] }>(`/api/me/yutai-items?includeUsed=${includeUsed ? "1" : "0"}`),
  createYutaiItem: (body: {
    stockCode: string;
    stockName?: string | undefined;
    recordDate: string;
    description: string;
    tierMinShares?: number | undefined;
    valueYen?: number | null | undefined;
    arrivedOn?: string | null | undefined;
    expiresOn?: string | null | undefined;
  }) => request<{ id: string }>("/api/me/yutai-items", { method: "POST", json: body }),
  updateYutaiItem: (id: string, body: Partial<Pick<YutaiItem, "stockName" | "description" | "valueYen" | "arrivedOn" | "expiresOn" | "state" | "usedOn" | "note">>) =>
    request<{ ok: true }>(`/api/me/yutai-items/${id}`, { method: "PATCH", json: body }),
  deleteYutaiItem: (id: string) => request<{ ok: true }>(`/api/me/yutai-items/${id}`, { method: "DELETE" }),

  summary: (year: string) => request<Summary>(`/api/me/summary?year=${year}`),
  summaryCsvUrl: (year: string) => `${base}/api/me/summary/csv?year=${year}`,
  longTerm: () => request<{ stocks: LongTermStock[] }>("/api/me/long-term"),
  setLongTermRecord: (body: { stockCode: string; recordDate: string; sharesHeld: number; qualified: boolean }) =>
    request<{ ok: true }>("/api/me/long-term/records", { method: "PUT", json: body }),
  clearLongTermRecord: (body: { stockCode: string; recordDate: string }) =>
    request<{ ok: true }>("/api/me/long-term/records", { method: "DELETE", json: body }),
  exportUrl: () => `${base}/api/me/backup/export`,
  importBackup: (body: unknown) => request<{ ok: true; counts: Record<string, number> }>("/api/me/backup/import", { method: "POST", json: body }),

  rewards: () => request<{ adFreeUntil: string | null; csvExportUntil: string | null; clientClaimAllowed: boolean }>("/api/me/rewards"),
  pushSubscriptions: () => request<{ subscriptions: Array<{ id: string; platform: string; createdAt: string; failures: number }> }>("/api/me/push"),
  deletePushSubscription: (id: string) => request<{ ok: true }>(`/api/me/push/${id}`, { method: "DELETE" }),

  admin: {
    status: () => request<AdminStatus>("/api/admin/status"),
    draftMaster: (body: { stockCode: string; url?: string; text?: string; imageBase64?: string; imageMediaType?: string }) =>
      request<{ draft: Record<string, unknown>; confidence: string }>("/api/admin/yutai-master/draft", { method: "POST", json: body }),
    yahooTest: (code: string) => request<Record<string, unknown>>(`/api/admin/providers/yahoo/test?code=${code}`),
    jobs: () => request<{ runs: JobRun[]; jobs: Array<{ name: string; crons: string[] }> }>("/api/admin/jobs"),
    runJob: (name: string) => request<{ ok: boolean; log: string[] }>(`/api/admin/jobs/${name}/run`, { method: "POST" }),
    reports: () => request<{ reports: Report[] }>("/api/admin/reports"),
    setReport: (id: string, status: "open" | "accepted" | "rejected") => request<{ ok: true }>(`/api/admin/reports/${id}`, { method: "PATCH", json: { status } }),
    upload: async (kind: string, file: File) => {
      const res = await fetch(`${base}/api/admin/upload/${kind}`, { method: "POST", credentials: "include", body: await file.arrayBuffer() });
      const body = (await res.json()) as { ok: boolean; log: string[]; error?: string };
      if (!res.ok) throw new ApiError(res.status, body.error ?? body.log?.join("\n") ?? res.statusText);
      return body;
    },
    masters: () => request<{ masters: Array<Record<string, unknown> & { stockCode: string; status: string; updatedAt: string }> }>("/api/admin/yutai-master"),
    master: (code: string) => request<{ master: Record<string, unknown> }>(`/api/admin/yutai-master/${code}`),
    putMaster: (code: string, body: unknown) => request<{ ok: true }>(`/api/admin/yutai-master/${code}`, { method: "PUT", json: body }),
    deleteMaster: (code: string) => request<{ ok: true }>(`/api/admin/yutai-master/${code}`, { method: "DELETE" }),
  },

  data: <T>(path: string) => request<T>(`/data/v1/${path}`),
};

export interface Summary {
  year: string;
  crossCount: number;
  notExecutedCount: number;
  estTotal: number;
  actualTotal: number;
  actualCount: number;
  yutaiCount: number;
  yutaiValue: number;
  netBenefit: number;
  byBroker: Record<string, { name: string; count: number; estFee: number; actualFee: number }>;
  byMonth: Record<string, { count: number; estTotal: number }>;
}

export interface LongTermRecord {
  recordDate: string;
  lastTradingDay: string;
  sharesHeld: number;
  qualified: boolean;
  manual: boolean;
  future: boolean;
}

export type LongTermStock =
  | { code: string; name: string | null; hasRule: false }
  | {
      code: string;
      name: string | null;
      hasRule: true;
      requiredShares: number;
      currentShares: number;
      streak: number;
      requiredCount: number;
      achieved: boolean;
      nextRecordDate: string | null;
      nextLastTradingDay: string | null;
      records: LongTermRecord[];
    };

export interface AdminStatus {
  now: string;
  jobs: Array<{ name: string; crons: string[]; last: { status: string; startedAt: string; finishedAt: string | null; detail: string | null } | null }>;
  counts: Record<string, number>;
  data: Record<string, string | null>;
  config: Record<string, boolean>;
}

export interface JobRun {
  id: string;
  job: string;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  detail: string | null;
}

export interface Report {
  id: string;
  stockCode: string;
  kind: string;
  message: string;
  sourceUrl: string | null;
  status: string;
  createdAt: string;
}

export interface CrossPlanInput {
  stockCode: string;
  stockName?: string | undefined;
  lastTradingDay: string;
  brokerSettingId: string;
  marginType: MarginType;
  shares: number;
  openDate?: string | undefined;
  closeDate?: string | undefined;
  priceAtPlan: number;
  estGyakuhibuPerShare?: number | null | undefined;
  dividendPerShare?: number | null | undefined;
  note?: string | undefined;
}

export interface CrossPlanActuals {
  actualGyakuhibu?: number | null;
  actualFee?: number | null;
  actualLoanCost?: number | null;
  actualDividendAdj?: number | null;
  actualCloseDate?: string | null;
  notExecuted?: boolean;
}
